import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  PlusIcon, 
  SearchIcon, 
  ClipboardListIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  Clock10Icon,
  AlertCircleIcon,
  FilterIcon,
  SlidersHorizontalIcon
} from "lucide-react";
import { RFQService, RFQRequest } from "@/lib/rfq-service";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

export default function BuyerRFQDashboard() {
  const [rfqs, setRfqs] = useState<RFQRequest[]>([]);
  const [filteredRfqs, setFilteredRfqs] = useState<RFQRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const rfqService = new RFQService();
  
  useEffect(() => {
    const loadRFQs = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const userRfqs = await rfqService.getUserRFQs(user.id);
        setRfqs(userRfqs);
        setFilteredRfqs(userRfqs);
      } catch (error) {
        console.error("Error loading RFQs:", error);
        toast({
          title: "Error",
          description: "Failed to load your RFQs. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadRFQs();
  }, [user]);
  
  // Apply filters when tab, search, or status filter changes
  useEffect(() => {
    if (!rfqs.length) return;
    
    let filtered = [...rfqs];
    
    // Apply tab filter
    if (activeTab !== "all") {
      filtered = filtered.filter(rfq => rfq.status === activeTab);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        rfq => rfq.title.toLowerCase().includes(query) || 
               rfq.description.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter (if not already filtered by tab)
    if (statusFilter && statusFilter !== "all" && activeTab === "all") {
      filtered = filtered.filter(rfq => rfq.status === statusFilter);
    }
    
    setFilteredRfqs(filtered);
  }, [rfqs, activeTab, searchQuery, statusFilter]);
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline" className="flex items-center gap-1">
          <Clock10Icon className="h-3.5 w-3.5" />
          Draft
        </Badge>;
      case "published":
        return <Badge className="bg-blue-500 flex items-center gap-1">
          <AlertCircleIcon className="h-3.5 w-3.5" />
          Active
        </Badge>;
      case "closed":
        return <Badge className="bg-yellow-500 flex items-center gap-1">
          <XCircleIcon className="h-3.5 w-3.5" />
          Closed
        </Badge>;
      case "fulfilled":
        return <Badge className="bg-green-500 flex items-center gap-1">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Fulfilled
        </Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };
  
  const getResponsesCount = async (rfqId: string): Promise<number> => {
    try {
      const responses = await rfqService.getRFQResponses(rfqId);
      return responses.length;
    } catch (error) {
      console.error("Error getting responses count:", error);
      return 0;
    }
  };
  
  if (!user) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>
            Please <Link to="/login" className="font-medium underline">log in</Link> to view your RFQs.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">RFQ Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage your quote requests and vendor responses</p>
        </div>
        
        <Button onClick={() => navigate("/create-rfq")} className="mt-4 md:mt-0">
          <PlusIcon className="h-4 w-4 mr-2" />
          Create New RFQ
        </Button>
      </div>
      
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative flex-grow">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Search RFQs..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex gap-2 items-center">
            <SlidersHorizontalIcon className="h-4 w-4 text-gray-500" />
            <Select 
              value={statusFilter} 
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="fulfilled">Fulfilled</SelectItem>
              </SelectContent>
            </Select>
            
            {(searchQuery || statusFilter !== "all") && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="all">
            All RFQs
          </TabsTrigger>
          <TabsTrigger value="draft">
            Drafts
          </TabsTrigger>
          <TabsTrigger value="published">
            Active
          </TabsTrigger>
          <TabsTrigger value="closed">
            Closed
          </TabsTrigger>
          <TabsTrigger value="fulfilled">
            Fulfilled
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab}>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-4 w-1/4 mt-2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3 mt-2" />
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-24" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : filteredRfqs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardListIcon className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-medium mb-2">No RFQs Found</h3>
                <p className="text-gray-500 text-center mb-6">
                  {searchQuery || statusFilter
                    ? "No RFQs match your current filters. Try adjusting your search criteria."
                    : "You haven't created any RFQs yet. Get started by creating your first quote request."}
                </p>
                {!(searchQuery || statusFilter) && (
                  <Button onClick={() => navigate("/create-rfq")}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create New RFQ
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredRfqs.map((rfq) => (
                <Card key={rfq.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{rfq.title}</CardTitle>
                        <CardDescription>
                          Created on {formatDate(rfq.created_at)}
                        </CardDescription>
                      </div>
                      <div>
                        {getStatusBadge(rfq.status)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="line-clamp-2 text-gray-600">
                      {rfq.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
                      <div>
                        <span className="text-gray-500">Quantity:</span>{" "}
                        <span className="font-medium">{rfq.quantity}</span>
                      </div>
                      
                      {rfq.expiry_date && (
                        <div>
                          <span className="text-gray-500">Expires:</span>{" "}
                          <span className="font-medium">{formatDate(rfq.expiry_date)}</span>
                        </div>
                      )}
                      
                      {rfq.delivery_date_required && (
                        <div>
                          <span className="text-gray-500">Delivery needed by:</span>{" "}
                          <span className="font-medium">{formatDate(rfq.delivery_date_required)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4 flex justify-between">
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/buyer-dashboard/rfqs/${rfq.id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                    
                    <div className="flex items-center">
                      {rfq.status === "published" && (
                        <Badge variant="outline" className="bg-green-50">
                          Active
                        </Badge>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
